import React, { useMemo } from "react";
import { Empty, Typography } from "antd";
import { Box as Bed, Calendar, ChevronLeft, ChevronRight, Globe, Home, Info, Package } from "react-feather";
import dayjs from "dayjs";
import "../distributionPreview.css";

const { Text } = Typography;

const currencySymbol = (code) => {
  if (code === "INR") return "₹";
  if (code === "GBP") return "£";
  if (code === "USD") return "$";
  if (code === "EUR") return "€";
  return `${code || ""} `;
};

const money = (value, currency) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return "—";
  return `${currencySymbol(currency)}${n.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
};

const priceLengthClass = (value, currency) => {
  const text = money(value, currency);
  if (text === "—") return "";
  if (text.length >= 10) return "is-very-long";
  if (text.length >= 8) return "is-long";
  return "";
};

const rateValues = (row) => {
  if (!row) return [];
  return [
    row?.occ1_rate,
    row?.occ2_rate,
    ...Object.values(row?.guest_wise_rates || {}),
  ]
    .map(Number)
    .filter((n) => Number.isFinite(n) && n >= 0);
};

function getOtaSummary(room, date) {
  const candidates = [];
  (room?.ratePlans || []).forEach((rp) => {
    const raw = rp?.rates?.[date];
    const values =
      raw && typeof raw === "object"
        ? Object.values(raw)
        : [raw];

    values.forEach((value) => {
      const amount = Number(value);
      if (Number.isFinite(amount) && amount > 0) {
        candidates.push({
          amount,
          source: "OTA rate",
          title: rp?.title || "OTA Rate Plan",
        });
      }
    });
  });

  if (!candidates.length) return null;
  return candidates.sort((a, b) => a.amount - b.amount)[0];
}

/**
 * Mirrors Booking Engine Manual Inventory's consolidated-rate mental model:
 * 1) Any date override present => consolidated value comes from date overrides.
 * 2) Otherwise use the lowest active master BE rate.
 * 3) A legacy/base override_rate is a valid date rate when no BE plan exists.
 */
export function getDirectSummary(rateOptions, date, beReadState) {
  const overrides = [];
  const calendarRow = beReadState?.rowsByDate?.[date] || null;

  if (
    calendarRow?.override_rate !== null &&
    calendarRow?.override_rate !== undefined &&
    calendarRow?.override_rate !== ""
  ) {
    const amount = Number(calendarRow.override_rate);
    if (Number.isFinite(amount) && amount >= 0) {
      overrides.push({
        amount,
        source: "Date override",
        title: "Base date rate",
      });
    }
  }

  (beReadState?.overridesByDate?.[date] || []).forEach((row) => {
    const option = (rateOptions || []).find(
      (rate) => Number(rate?.rate_id ?? rate?.id) === Number(row?.rate_id)
    );
    rateValues(row).forEach((amount) => {
      overrides.push({
        amount,
        source: "Date override",
        title:
          option?.display_label ||
          option?.rate_name ||
          option?.title ||
          option?.name ||
          option?.meal_plan ||
          "Booking Engine Rate",
      });
    });
  });

  if (overrides.length) {
    return overrides.sort((a, b) => a.amount - b.amount)[0];
  }

  const master = [];
  (rateOptions || []).forEach((rate) => {
    rateValues(rate).forEach((amount) => {
      master.push({
        amount,
        source: "Master rate",
        title:
          rate?.display_label ||
          rate?.rate_name ||
          rate?.title ||
          rate?.name ||
          rate?.meal_plan ||
          "Booking Engine Rate",
      });
    });
  });

  if (master.length) {
    return master.sort((a, b) => a.amount - b.amount)[0];
  }

  const defaults = beReadState?.roomTypeDefaults || {};
  const roomTypeCandidates = [
    ["Single", defaults?.base_rate_single],
    ["Double", defaults?.base_rate_double],
  ]
    .map(([label, value]) => ({ label, amount: Number(value) }))
    .filter((item) => Number.isFinite(item.amount) && item.amount > 0);

  if (!roomTypeCandidates.length) return null;
  const lowest = roomTypeCandidates.sort((a, b) => a.amount - b.amount)[0];
  return {
    amount: lowest.amount,
    source: "Room Type price",
    title: `${lowest.label} default`,
  };
}

function availabilityState(room, date, local) {
  if (local?.availability !== undefined && local?.availability !== null) {
    return { known: true, value: Number(local.availability), source: "staged" };
  }

  const raw = room?.availability?.[date];
  const presence = room?.availability_source?.[date];

  if (presence === "missing") return { known: false, value: null, source: "missing" };
  if (presence === "stored" || presence === "channel_manager") {
    return { known: true, value: Number(raw), source: presence };
  }

  // Backward-compatible conservative fallback. Older /ari/grid responses filled
  // missing dates with zero, so a zero without presence metadata must NOT be
  // presented as a confirmed sell-out.
  const numeric = Number(raw);
  if (Number.isFinite(numeric) && numeric > 0) {
    return { known: true, value: numeric, source: "legacy-positive" };
  }
  return { known: false, value: null, source: "legacy-unknown" };
}

export default function DistributionCalendarPreview({
  grid,
  roomId,
  beRateOptions = [],
  beReadState = null,
  previewChanges = {},
  distributionHealth = null,
  currency = "INR",
  onOpenDay,
  onPrev,
  onNext,
  onPickStartDate,
  startDateLabel,
}) {
  const room = useMemo(
    () => (grid?.rooms || []).find((r) => String(r.pms_room_type_id) === String(roomId)) || (grid?.rooms || [])[0],
    [grid, roomId]
  );

  const dates = grid?.dates || [];

  if (!room || !dates.length) {
    return <Empty description="No mapped room inventory available for this period." />;
  }

  return (
    <div className="dist-preview-shell dist-preview-v3">
      <div className="dist-preview-toolbar dist-preview-toolbar-compact">
        <div>
          <div className="dist-preview-kicker">Distribution Calendar</div>
          <div className="dist-preview-roomline">
            <Package size={16} />
            <strong>{room.name}</strong>
            <span>{Number(room.total_rooms || 0)} physical rooms</span>
          </div>
        </div>

        <div className="dist-preview-date-nav">
          <button type="button" className="dist-icon-btn" onClick={onPrev} aria-label="Previous range">
            <ChevronLeft size={17} />
          </button>
          <button type="button" className="dist-date-pill" onClick={onPickStartDate}>
            <Calendar size={15} />
            {startDateLabel}
          </button>
          <button type="button" className="dist-icon-btn" onClick={onNext} aria-label="Next range">
            <ChevronRight size={17} />
          </button>
        </div>
      </div>

      <div className="dist-operating-model-inline dist-operating-model-simple">
        <Info size={14} />
        <strong>One inventory</strong>
        <span className="dist-model-chip">Direct + all OTAs</span>
        <span className="dist-model-copy">
          Tap any date to manage selling status and pricing.
        </span>
      </div>

      <div className="dist-calendar-grid dist-calendar-desktop">
        {dates.map((date) => {
          const key = `${room.pms_room_type_id}|${date}`;
          const local = previewChanges[key] || {};
          const avl = availabilityState(room, date, local);
          const restriction = room?.restrictions?.[date] || null;
          const stopSell = local.stopSell ?? restriction?.stop_sell ?? false;
          const minStay =
            local.minStay ??
            restriction?.min_stay ??
            restriction?.min_stay_arrival ??
            restriction?.min_stay_through ??
            null;
          const cta = local.cta ?? restriction?.closed_to_arrival ?? false;
          const ctd = local.ctd ?? restriction?.closed_to_departure ?? false;
          const otaSummary = getOtaSummary(room, date);
          const directSummary = getDirectSummary(beRateOptions, date, beReadState);

          const soldOut = avl.known && Number(avl.value) === 0;
          const isToday = dayjs(date).isSame(dayjs(), "day");
          const isWeekend = [0, 6].includes(dayjs(date).day());
          const statusClass = stopSell
            ? "stop"
            : !avl.known
              ? "unset"
              : soldOut
                ? "soldout"
                : "live";
          const statusLabel = stopSell
            ? "Stop Sell"
            : !avl.known
              ? "Not set"
              : soldOut
                ? "Sold Out"
                : "Selling";
          const hasRestrictions =
            Boolean(minStay) || Boolean(cta) || Boolean(ctd);

          return (
            <button
              key={date}
              type="button"
              className={[
                "dist-day-card",
                isToday ? "is-today" : "",
                isWeekend ? "is-weekend" : "",
                local.__dirty ? "is-preview-edited" : "",
              ].join(" ")}
              onClick={() => onOpenDay?.({ room, date })}
            >
              <div className="dist-day-head">
                <div className="dist-day-date">
                  <span className="dist-day-dow">{dayjs(date).format("ddd")}</span>
                  <span className="dist-day-number">{dayjs(date).format("DD")}</span>
                </div>
                <span className={`dist-status ${statusClass}`}>{statusLabel}</span>
              </div>

              <div className="dist-simple-availability">
                {avl.known ? (
                  <div className="dist-avl-stat icon-only" aria-label={`${avl.value} ${Number(avl.value) === 1 ? "room" : "rooms"} available`}>
                    <span className="dist-avl-icon-wrap"><Bed size={14} aria-hidden="true" /></span>
                    <strong>{avl.value}</strong>
                  </div>
                ) : (
                  <div className="dist-avl-stat is-empty icon-only" aria-label="Availability not set">
                    <span className="dist-avl-icon-wrap"><Bed size={14} aria-hidden="true" /></span>
                    <strong>—</strong>
                  </div>
                )}
              </div>

              <div className="dist-simple-pricing">
                <div className="dist-price-box dist-price-box-direct">
                  <span className="dist-price-label direct">
                    <Home size={11} aria-hidden="true" />
                    Direct
                  </span>
                  <strong
                    className={`dist-price-value direct ${
                      directSummary
                        ? priceLengthClass(directSummary.amount, currency)
                        : ""
                    }`}
                    title={
                      directSummary
                        ? money(directSummary.amount, currency)
                        : "Direct price not set"
                    }
                  >
                    {directSummary ? money(directSummary.amount, currency) : "—"}
                  </strong>
                </div>
                <div className="dist-price-box dist-price-box-ota">
                  <span className="dist-price-label ota">
                    <Globe size={11} aria-hidden="true" />
                    OTA
                  </span>
                  <strong
                    className={`dist-price-value ota ${
                      otaSummary
                        ? priceLengthClass(otaSummary.amount, currency)
                        : ""
                    }`}
                    title={
                      otaSummary
                        ? money(otaSummary.amount, currency)
                        : "OTA price not set"
                    }
                  >
                    {otaSummary ? money(otaSummary.amount, currency) : "—"}
                  </strong>
                </div>
              </div>

              {hasRestrictions && (
                <div className="dist-rule-chips" aria-label="Active selling restrictions">
                  {minStay && <span>Min {minStay}</span>}
                  {cta && <span>CTA</span>}
                  {ctd && <span>CTD</span>}
                </div>
              )}

              {local.__dirty && <span className="dist-preview-change-dot">Unsaved</span>}
            </button>
          );
        })}
      </div>

      <div className="dist-mobile-day-list">
        {dates.map((date) => {
          const key = `${room.pms_room_type_id}|${date}`;
          const local = previewChanges[key] || {};
          const avl = availabilityState(room, date, local);
          const restriction = room?.restrictions?.[date] || null;
          const stopSell = local.stopSell ?? restriction?.stop_sell ?? false;
          const minStay =
            local.minStay ??
            restriction?.min_stay ??
            restriction?.min_stay_arrival ??
            restriction?.min_stay_through ??
            null;
          const cta = local.cta ?? restriction?.closed_to_arrival ?? false;
          const ctd = local.ctd ?? restriction?.closed_to_departure ?? false;
          const otaSummary = getOtaSummary(room, date);
          const directSummary = getDirectSummary(beRateOptions, date, beReadState);
          const soldOut = avl.known && Number(avl.value) === 0;
          const statusClass = stopSell
            ? "stop"
            : !avl.known
              ? "unset"
              : soldOut
                ? "soldout"
                : "live";
          const statusLabel = stopSell
            ? "Stop Sell"
            : !avl.known
              ? "Not set"
              : soldOut
                ? "Sold Out"
                : "Selling";

          return (
            <button
              key={`mobile-${date}`}
              type="button"
              className={[
                "dist-mobile-day",
                dayjs(date).isSame(dayjs(), "day") ? "is-today" : "",
                local.__dirty ? "is-preview-edited" : "",
              ].join(" ")}
              onClick={() => onOpenDay?.({ room, date })}
            >
              <div className="dist-mobile-day-date">
                <span>{dayjs(date).format("ddd")}</span>
                <strong>{dayjs(date).format("DD")}</strong>
                <small>{dayjs(date).format("MMM")}</small>
              </div>

              <div className="dist-mobile-day-main">
                <div className="dist-mobile-day-status">
                  <span className={`dist-status ${statusClass}`}>{statusLabel}</span>
                  <strong
                    className={`dist-mobile-availability-copy ${avl.known ? "" : "is-empty"}`}
                    aria-label={
                      avl.known
                        ? `${avl.value} ${Number(avl.value) === 1 ? "room" : "rooms"} available`
                        : "Availability not set"
                    }
                  >
                    <Bed size={11} aria-hidden="true" />
                    <span>{avl.known ? avl.value : "—"}</span>
                  </strong>
                </div>

                <div className="dist-mobile-day-rates">
                  <div className="dist-mobile-rate direct">
                    <span><Home size={10} aria-hidden="true" /> Direct</span>
                    <b
                      className={
                        directSummary
                          ? priceLengthClass(directSummary.amount, currency)
                          : ""
                      }
                    >
                      {directSummary ? money(directSummary.amount, currency) : "—"}
                    </b>
                  </div>
                  <div className="dist-mobile-rate ota">
                    <span><Globe size={10} aria-hidden="true" /> OTA</span>
                    <b
                      className={
                        otaSummary
                          ? priceLengthClass(otaSummary.amount, currency)
                          : ""
                      }
                    >
                      {otaSummary ? money(otaSummary.amount, currency) : "—"}
                    </b>
                  </div>
                </div>

                {(minStay || cta || ctd) && (
                  <div className="dist-rule-chips">
                    {minStay && <span>Min {minStay}</span>}
                    {cta && <span>CTA</span>}
                    {ctd && <span>CTD</span>}
                  </div>
                )}
              </div>

              <ChevronRight size={18} className="dist-mobile-day-chevron" />
            </button>
          );
        })}
      </div>

      <div className="dist-preview-footnote dist-preview-footnote-compact dist-calendar-legend">
        <Text type="secondary">
          <b>Not set</b> = no Channel Manager availability yet · <b>0 rooms</b> = Sold Out ·
          Stop Sell is an explicit closure. Tap a date for rate source and detailed controls.
          {beReadState?.status === "unmapped"
            ? " Direct editing is blocked until the Booking Engine room-type ID matches."
            : ""}
          {beReadState?.status === "error"
            ? ` ${beReadState?.message || "Direct pricing could not be loaded."}`
            : ""}
          {distributionHealth?.status === "partial"
            ? " Attention: the last save needs review."
            : ""}
        </Text>
      </div>
    </div>
  );
}
